import { useCallback, useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { db, GetOneUser, normalizeUserRecord, Task } from "../firebase-config";

const userCache = new Map<string, Task | null>();
const pendingRequests = new Map<string, Promise<Task | null>>();

const loadUserRecord = async (
  userId: string,
  forceRefresh = false,
): Promise<Task | null> => {
  if (!forceRefresh && userCache.has(userId)) {
    return userCache.get(userId) ?? null;
  }

  const pendingRequest = !forceRefresh ? pendingRequests.get(userId) : null;
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = GetOneUser(userId)
    .then((userRecord) => {
      userCache.set(userId, userRecord);
      pendingRequests.delete(userId);
      return userRecord;
    })
    .catch((error) => {
      pendingRequests.delete(userId);
      throw error;
    });

  pendingRequests.set(userId, request);
  return request;
};

export const invalidateCurrentUserData = (userId?: string) => {
  if (userId) {
    userCache.delete(userId);
    pendingRequests.delete(userId);
    return;
  }

  userCache.clear();
  pendingRequests.clear();
};

const useCurrentUserData = () => {
  const { user } = useAuth();
  const [userData, setUserData] = useState<Task | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshUserData = useCallback(async () => {
    if (!user?.uid) {
      setUserData(null);
      return null;
    }

    setIsLoading(true);

    try {
      const userRecord = await loadUserRecord(user.uid, true);
      setUserData(userRecord);
      return userRecord;
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setUserData(null);
      setIsLoading(false);
      return;
    }

    const userId = user.uid;
    if (userCache.has(userId)) {
      setUserData(userCache.get(userId) ?? null);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    const unsubscribe = onSnapshot(
      doc(db, "Usuarios", userId),
      (snapshot) => {
        const userRecord = snapshot.exists()
          ? normalizeUserRecord(snapshot.id, snapshot.data())
          : null;

        userCache.set(userId, userRecord);
        pendingRequests.delete(userId);

        if (userRecord) {
          localStorage.setItem(userId, JSON.stringify(userRecord));
        } else {
          localStorage.removeItem(userId);
        }

        setUserData(userRecord);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error subscribing to current user:", error);
        setIsLoading(false);
        void refreshUserData();
      },
    );

    return () => unsubscribe();
  }, [refreshUserData, user?.uid]);

  return { userData, isLoading, refreshUserData };
};

export default useCurrentUserData;